if (!customElements.get('tlg-wishlist')) {
  customElements.define('tlg-wishlist', class extends HTMLElement {
    connectedCallback() {
      this.key = 'tlg-saved-pieces-v1';
      this.dialog = this.querySelector('dialog');
      this.list = this.querySelector('[data-tlg-list]');
      this.empty = this.querySelector('[data-tlg-empty]');
      this.toast = this.querySelector('[data-tlg-toast]');
      this.feedback = this.querySelector('[data-tlg-feedback]');
      this.items = this.read();
      this.clickHandler = event => this.click(event);
      this.storageHandler = event => { if (event.key === this.key || event.key === null) { this.items = this.read(); this.sync(); if (this.dialog.open) this.render(); } };
      this.pageShowHandler = event => {
        if (!event.persisted) return;
        this.items = this.read(); this.sync();
        if (this.dialog.open) this.render();
      };
      this.closeHandler = () => { document.documentElement.classList.remove('tlg-saved-open'); this.request?.abort(); this.opener?.isConnected && this.opener.focus(); };
      document.addEventListener('click', this.clickHandler);
      window.addEventListener('storage', this.storageHandler);
      window.addEventListener('pageshow', this.pageShowHandler);
      this.dialog.addEventListener('close', this.closeHandler);
      this.observer = new MutationObserver(records => {
        if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches('[data-tlg-save], [data-tlg-open]') || node.querySelector('[data-tlg-save], [data-tlg-open]'))))) this.sync();
      });
      this.observer.observe(document.body, { childList:true, subtree:true });
      this.sync();
    }
    read() {
      try {
        const value = JSON.parse(localStorage.getItem(this.key) || '[]');
        if (!Array.isArray(value)) return [];
        const unique = new Set();
        return value.filter(item => {
          if (!item || typeof item.handle !== 'string' || !/^[a-z0-9][a-z0-9-]{0,254}$/.test(item.handle) || unique.has(item.handle)) return false;
          unique.add(item.handle); return true;
        }).slice(0,100).map(item => ({ handle:item.handle, title:typeof item.title === 'string' ? item.title.slice(0,300) : item.handle, image:this.imageURL(item.image) }));
      } catch { return []; }
    }
    imageURL(value) {
      if (typeof value !== 'string' || !value) return '';
      try {
        const url = new URL(value, location.origin);
        return url.protocol === 'https:' && (url.origin === location.origin || url.hostname === 'cdn.shopify.com') ? url.href : '';
      } catch { return ''; }
    }
    productURL(handle) { return `${this.dataset.root.replace(/\/$/, '')}/products/${encodeURIComponent(handle)}`; }
    announce(message) {
      clearTimeout(this.timer);
      const target = this.dialog.open ? this.feedback : this.toast;
      target.textContent = message;
      target.hidden = false;
      this.timer = setTimeout(() => { target.hidden = true; }, 6500);
    }
    save(items, message) {
      try { localStorage.setItem(this.key, JSON.stringify(items)); }
      catch { this.announce(this.dataset.error); return false; }
      this.items = items;
      this.sync();
      this.announce(message);
      return true;
    }
    click(event) {
      const button = event.target.closest('button');
      if (!button) return;
      if (button.matches('[data-tlg-open]')) {
        this.open(button);
      } else if (button.matches('[data-tlg-close]')) {
        this.dialog.close();
      } else if (button.matches('[data-tlg-save], [data-tlg-remove]')) {
        event.preventDefault(); event.stopPropagation();
        const handle = button.dataset.tlgSave || button.dataset.tlgRemove;
        if (!/^[a-z0-9][a-z0-9-]{0,254}$/.test(handle)) return;
        this.items = this.read();
        const index = this.items.findIndex(item => item.handle === handle);
        if (index >= 0) {
          if (this.save(this.items.filter(item => item.handle !== handle), this.dataset.removed) && this.dialog.open) {
            this.render();
            const removers = this.list.querySelectorAll('[data-tlg-remove]');
            (removers[Math.min(index,removers.length - 1)] || this.querySelector('[data-tlg-close]')).focus();
          }
        } else if (this.items.length >= 100) {
          this.announce(this.dataset.limit);
        } else if (button.hasAttribute('data-tlg-save')) {
          this.save([...this.items, { handle, title:button.dataset.title || handle, image:this.imageURL(button.dataset.image) }], this.dataset.added);
        }
      }
    }
    async open(button) {
      if (typeof this.dialog.showModal !== 'function' || this.opening || this.dialog.open) return;
      this.opening = true;
      try {
        const drawer = button.closest('header-drawer');
        this.opener = drawer?.triggerButton || button;
        if (drawer?.classList.contains('is-open')) {
          if (drawer.isAnimating && window.theme?.waitForAnimationEnd) await window.theme.waitForAnimationEnd(drawer.drawerInner);
          drawer.dispatchEvent(new CustomEvent('theme:drawer:close', { bubbles:true }));
          if (window.theme?.waitForAnimationEnd) await window.theme.waitForAnimationEnd(drawer.drawerInner);
          if (drawer.classList.contains('is-open')) return;
        }
        if (!this.isConnected) return;
        this.items = this.read(); this.sync();
        this.feedback.hidden = true;
        this.dialog.showModal(); document.documentElement.classList.add('tlg-saved-open'); this.render();
      } finally { this.opening = false; }
    }
    sync() {
      const saved = new Set(this.items.map(item => item.handle));
      document.querySelectorAll('[data-tlg-save]').forEach(button => {
        const active = saved.has(button.dataset.tlgSave);
        const label = active ? this.dataset.remove : this.dataset.save;
        button.hidden = false;
        button.setAttribute('aria-pressed', String(active));
        button.setAttribute('aria-label', `${label}: ${button.dataset.title}`);
        const text = button.querySelector('[data-tlg-save-label]');
        if (text) text.textContent = label;
      });
      document.querySelectorAll('[data-tlg-open]').forEach(button => {
        button.hidden = typeof this.dialog.showModal !== 'function';
        const count = button.querySelector('.tlg-save__count');
        if (count) { count.textContent = this.items.length; count.hidden = this.items.length === 0; }
      });
    }
    render() {
      this.request?.abort();
      this.request = new AbortController();
      this.list.replaceChildren();
      this.empty.hidden = this.items.length > 0;
      const entries = this.items.map(item => {
        const row = document.createElement('li'); row.className = 'tlg-saved__item';
        const imageLink = document.createElement('a'); imageLink.href = this.productURL(item.handle); imageLink.tabIndex = -1; imageLink.setAttribute('aria-hidden','true');
        const image = document.createElement('img'); image.className = 'tlg-saved__image'; image.alt = ''; image.loading = 'lazy'; image.width = 104; image.height = 139;
        if (item.image) image.src = item.image;
        image.addEventListener('error', () => { image.removeAttribute('src'); }, { once:true });
        imageLink.append(image);
        const content = document.createElement('div');
        const link = document.createElement('a'); link.className = 'tlg-saved__name'; link.href = this.productURL(item.handle); link.textContent = item.title;
        const unavailable = document.createElement('p'); unavailable.className = 'tlg-saved__unavailable'; unavailable.textContent = this.dataset.unavailable; unavailable.hidden = true;
        const remove = document.createElement('button'); remove.type = 'button'; remove.dataset.tlgRemove = item.handle; remove.textContent = this.dataset.removeShort; remove.setAttribute('aria-label', `${this.dataset.remove}: ${item.title}`);
        content.append(link,unavailable,remove); row.append(imageLink,content); this.list.append(row);
        return { item, row, image, imageLink, link, unavailable, remove };
      });
      const signal = this.request.signal;
      const queue = [...entries];
      const worker = async () => {
        while (queue.length && !signal.aborted) {
          const entry = queue.shift();
          try {
            const response = await fetch(`${this.productURL(entry.item.handle)}.js`, { signal, credentials:'same-origin', headers:{ Accept:'application/json' } });
            if (signal.aborted) return;
            if (response.status === 404) {
              entry.link.removeAttribute('href'); entry.imageLink.removeAttribute('href'); entry.unavailable.hidden = false;
            } else if (response.ok) {
              const product = await response.json();
              if (typeof product.title === 'string') entry.link.textContent = product.title;
              const src = this.imageURL(product.featured_image);
              if (src) entry.image.src = src;
            }
          } catch { }
        }
      };
      Promise.all(Array.from({ length:Math.min(3,queue.length) }, worker));
    }
    disconnectedCallback() {
      document.removeEventListener('click', this.clickHandler);
      window.removeEventListener('storage', this.storageHandler);
      window.removeEventListener('pageshow', this.pageShowHandler);
      this.dialog.removeEventListener('close', this.closeHandler);
      this.observer.disconnect(); this.request?.abort(); clearTimeout(this.timer);
      document.documentElement.classList.remove('tlg-saved-open');
    }
  });
}
